'use client';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { ProductStatusText } from '@eliadmin/core/src/components/product/product-status-text';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';

import { LuStar } from 'react-icons/lu';
import { ProductQuantity } from '@eliadmin/core/src/components/product/detail/product-quantity';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ProductReplacedBy } from './detail/product-replaced-by';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { AskForPriceButton } from '@eliadmin/core/src/components/product/ask-for-price-button';
import { ProductAccessories } from '@eliadmin/core/src/components/product/detail/product-accesories';
import { ProductAccessoriesVariants } from '@eliadmin/core/src/components/product/detail/product-accessories-variants/product-accessories-variants';
import { ProductVariants } from '@eliadmin/core/src/components/product/detail/variants/product-variants';

export function ProductInfo({ product }: { product: ProductDetailData }) {
  const { t } = useTranslations();
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const priceWithoutVatFirst = useGlobalStore(
    s => s.templateSettings.product_detail?.price_without_vat_first?.enabled,
  );
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product, {
    isWholesaler,
    priceWithoutVatFirst,
  });

  const formatPrice = (price: number) => {
    const [integerPart, decimalPart] = price.toFixed(2).split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${formattedInteger}.${decimalPart}`;
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 bg-[#F5F5F5] p-6 sm:gap-8 sm:p-8 lg:gap-10 lg:p-10 xl:gap-12 xl:p-12">
      <Breadcrumbs breadcrumb={product.breadcrumb} product={{ name: product.name }} />
      <div className="flex w-full flex-col items-start justify-start gap-6">
        <div className="flex w-full items-center justify-start gap-2">
          {[...Array(5)].map((_, i) => (
            <LuStar key={i} className="h-5 w-5 fill-[#FFD400] text-[#FFD400]" />
          ))}
          <p className="text-sm font-medium text-black">(5.0)</p>
        </div>
        <h1 className="text-3xl font-normal tracking-tighter text-black sm:text-4xl lg:text-5xl">
          {product.name}
        </h1>
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-6">
        {priceDisplay.priceOnRequest ? (
          <AskForPriceButton product={product} variant={selectedVariant ?? undefined} />
        ) : (
          <div className="flex w-full flex-col items-start justify-start gap-2">
            {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null && (
              <p className="text-sm text-[#B2B2B2]">
                {t('text1926')} {formatPrice(priceDisplay.mocPrice)} €
              </p>
            )}
            <div className="flex items-baseline justify-start gap-4 lg:gap-6">
              <strong className="text-2xl font-semibold tracking-tighter text-primary sm:text-3xl">
                {formatPrice(priceDisplay.current!)} €
              </strong>
              {priceDisplay.showPreviousPrice && priceDisplay.beforeDiscount != null && (
                <p className="text-lg font-light text-black line-through sm:text-xl lg:text-2xl">
                  {formatPrice(priceDisplay.beforeDiscount)} €
                </p>
              )}
            </div>
            {priceDisplay.secondary != null && (
              <p className="text-sm font-normal tracking-tighter text-[#B2B2B2]">
                {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
                {formatPrice(priceDisplay.secondary)} €{' '}
                {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
              </p>
            )}
          </div>
        )}
        {product.status === 'ended' || product.status === 'arrival' ? (
          <ProductStatusText status={product.status} />
        ) : product.availability.id === 1 ? (
          <div className="flex items-center justify-start gap-2 text-base font-medium tracking-tighter text-[#0C9F11] sm:text-lg">
            <Image
              src={'/images/available.svg'}
              alt={product.availability.name}
              width={24}
              height={24}
            />
            {product.availability.name}
          </div>
        ) : (
          <p className="text-base font-medium tracking-tighter text-[#FF0000] sm:text-lg">
            {product.availability.name}
          </p>
        )}
        <ProductVariants />
        <ProductAccessoriesVariants />
        <ProductAccessories />
        {!priceDisplay.priceOnRequest && <ProductQuantity />}
      </div>
      {selectedVariant?.replaced_by && (
        <ProductReplacedBy replacedBy={selectedVariant.replaced_by} />
      )}
      <p className="w-full border-t border-t-border pt-6 text-sm font-medium tracking-tighter text-black/70">
        {t('text228')} {product.code}
      </p>
    </div>
  );
}
